import { CENT, parseMoney, type Money } from '@mybooks/ledger';

/** API amounts are decimal strings with 4 places. Parse them as bigint Money, never as floats. */
export const money = (value: string | null | undefined): Money => (value ? parseMoney(value) : 0n);

/**
 * Each currency's own symbol, so a dollar amount says which dollar (APP-DESIGN.md 9.4): $ is the US
 * dollar, CA$, A$, NZ$ and MX$ the others, as Intl.NumberFormat writes them in en-US. A currency
 * without a symbol of its own shows its code ("CHF 12.00"). The rand is "R1,000.00" (PRACTICE-DESIGN.md 8).
 */
const SYMBOLS: Record<string, string> = { USD: '$', ZAR: 'R', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$', NZD: 'NZ$', MXN: 'MX$' };
export function currencySymbol(currency: string): string {
  const known = SYMBOLS[currency];
  if (known !== undefined) return known;
  let symbol = currency;
  try {
    symbol = new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'symbol' }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? currency;
  } catch { /* not a currency code Intl knows */ }
  SYMBOLS[currency] = /^[A-Z]{3}$/.test(symbol) ? `${symbol} ` : symbol;
  return SYMBOLS[currency] as string;
}

/** $1,234.56, rounding half away from zero to the cent. Negative amounts get a leading minus. */
export function fmtMoney(value: Money | string | null | undefined, currency = 'USD'): string {
  let units = typeof value === 'bigint' ? value : money(value);
  const negative = units < 0n;
  if (negative) units = -units;
  const cents = (units + CENT / 2n) / CENT;
  const whole = (cents / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const frac = (cents % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${currencySymbol(currency)}${whole}.${frac}`;
}

/** Accounting style for reports: negatives in parentheses. */
export function fmtReport(value: Money | string | null | undefined, currency = 'USD'): string {
  const units = typeof value === 'bigint' ? value : money(value);
  return units < 0n ? `(${fmtMoney(-units, currency)})` : fmtMoney(units, currency);
}

/** What a person typed ("1,234.5", "$20") as a decimal string for the API, or null if it is not an amount. */
export function parseAmount(text: string): string | null {
  const m = /^\s*-?\s*\$?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,4}))?\s*$/.exec(text);
  if (!m) return null;
  const negative = text.trim().startsWith('-');
  return `${negative ? '-' : ''}${(m[1] as string).replace(/,/g, '')}.${(m[2] ?? '').padEnd(2, '0')}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Apr 7, 2026 from an ISO date (or timestamp). Dates are calendar dates, so no time zone shifts. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
}
export const fmtShortDate = (iso: string) => fmtDate(iso).replace(/, \d{4}$/, '');

/** Today in the browser's calendar, as YYYY-MM-DD. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
export const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
